import { syncAPI } from 'api/sync'
import type { PushOfflineOrderResult } from 'api/sync'
import { flushPosAuditEvents, logPosAuditEvent } from 'services/posAuditLog'
import { publishPosDeviceSyncHeartbeat } from 'services/posDeviceSyncHeartbeat'
import * as posStore from 'store/posStore'
import { replayQueue } from 'slices/offlineMiddleware'
import { store } from 'slices/index'
import { fetchCategories } from 'slices/categoriesSlice'
import { fetchProducts } from 'slices/productsSlice'
import { fetchCustomers } from 'slices/customersSlice'
import { fetchSuppliers } from 'slices/suppliersSlice'
import { fetchPurchases } from 'slices/purchasesSlice'
import { fetchExpenses } from 'slices/expensesSlice'
import { fetchDeliveries } from 'slices/deliveriesSlice'
import { checkConnectivity } from 'utils/connectivityCheck'
import { flushTableOps } from 'services/TableSyncService'
import { flushPendingTakeawayJournal } from 'services/flushPendingTakeawayJournal'
import { rememberUnpaidTakeawayOfflineLease, readOfflineModeFromPlanAccess } from 'store/unpaidTakeawayOfflineLease'
import { getActiveRegisterSessionId, getSelectedPosRegisterId } from 'utils/registerSessionContext'
import showWarning from 'helpers/WarningNotification'
import { isSuspiciousOfflineSyncRow } from 'utils/offlineSyncMismatch'
import i18n from 'i18next'
import { recordClientError } from 'services/clientDiagnostics'

let pushIntervalId: ReturnType<typeof setInterval> | null = null
let onlineHandler: (() => void) | null = null
let syncing = false
let pushMutex: Promise<unknown> = Promise.resolve()
let lastModuleSyncAt = 0
const recentPushConfirmations = new Map<
  string,
  { row: PushOfflineOrderResult; confirmedAt: number }
>()

const SYNCING_STALE_MS = 5 * 60 * 1000
const PUSH_CONFIRMATION_TTL_MS = 10 * 60 * 1000
const MAX_RECENT_PUSH_CONFIRMATIONS = 1_000
/** Push / heartbeat / queue flush cadence */
const AUTO_SYNC_INTERVAL_MS = 60_000
const AUTO_SYNC_JITTER_MS = 15_000
/** Full Redux list fan-out is much heavier — run rarely and only from the leader tab */
const MODULE_SYNC_INTERVAL_MS = 5 * 60_000
const TAB_LOCK_KEY = 'erp_sync_leader_tab'
const TAB_LOCK_STALE_MS = 90_000

function getTabId(): string {
  try {
    const existing = sessionStorage.getItem('erp_tab_id')
    if (existing) return existing
    const id = crypto.randomUUID()
    sessionStorage.setItem('erp_tab_id', id)
    return id
  } catch {
    return 'fallback-tab'
  }
}

/** Only one open shell should hammer the 7 list endpoints. */
function claimSyncLeaderTab(): boolean {
  try {
    const id = getTabId()
    const now = Date.now()
    const raw = localStorage.getItem(TAB_LOCK_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; at?: number }
      if (parsed?.id && parsed.id !== id && typeof parsed.at === 'number' && now - parsed.at < TAB_LOCK_STALE_MS) {
        return false
      }
    }
    localStorage.setItem(TAB_LOCK_KEY, JSON.stringify({ id, at: now }))
    return true
  } catch {
    return true
  }
}

function shouldRunModuleFanOut(): boolean {
  if (typeof document !== 'undefined' && document.hidden) return false
  if (!claimSyncLeaderTab()) return false
  const now = Date.now()
  if (now - lastModuleSyncAt < MODULE_SYNC_INTERVAL_MS) return false
  lastModuleSyncAt = now
  return true
}

function withPushMutex<T>(fn: () => Promise<T>): Promise<T> {
  const run = pushMutex.then(fn, fn)
  pushMutex = run.then(
    () => undefined,
    () => undefined
  )
  return run
}

/**
 * A checkout and the background auto-sync share the same queue and mutex. If
 * auto-sync wins the mutex, it can commit and remove the row before the
 * checkout's direct push gets its turn. Keep the authoritative server result
 * long enough for the waiting checkout to consume instead of reporting a
 * false `persistence_lost` and inviting the cashier to sell the same cart
 * again under a new UUID.
 */
function rememberPushConfirmation(row: PushOfflineOrderResult): void {
  if (!row.client_uuid || row.sale_id == null) return
  const now = Date.now()
  recentPushConfirmations.set(row.client_uuid, { row: { ...row }, confirmedAt: now })

  for (const [clientUuid, confirmation] of Array.from(recentPushConfirmations.entries())) {
    if (now - confirmation.confirmedAt > PUSH_CONFIRMATION_TTL_MS) {
      recentPushConfirmations.delete(clientUuid)
    }
  }
  while (recentPushConfirmations.size > MAX_RECENT_PUSH_CONFIRMATIONS) {
    const oldest = recentPushConfirmations.keys().next().value as string | undefined
    if (!oldest) break
    recentPushConfirmations.delete(oldest)
  }
}

function recentPushConfirmation(clientUuid: string): PushOfflineOrderResult | undefined {
  const confirmation = recentPushConfirmations.get(clientUuid)
  if (!confirmation) return undefined
  if (Date.now() - confirmation.confirmedAt > PUSH_CONFIRMATION_TTL_MS) {
    recentPushConfirmations.delete(clientUuid)
    return undefined
  }
  return { ...confirmation.row }
}

function isSyncingStale(order: posStore.PendingOfflineOrder): boolean {
  if (order.status !== 'syncing') return false
  if (!order.sync_started_at) return true
  return Date.now() - new Date(order.sync_started_at).getTime() > SYNCING_STALE_MS
}

export async function pull(branchId?: string | null): Promise<void> {
  if (!(await checkConnectivity())) return
  const res = await syncAPI.pullFullPosData(branchId ?? undefined)
  if (res.status === 'success' && res.data) {
    const catalogBranchId = res.data.branch_id ?? branchId ?? undefined
    await posStore.setCatalog(res.data, { branchId: catalogBranchId })
    try {
      let planAccess: unknown = null
      const raw = localStorage.getItem('authUser')
      if (raw) {
        const parsed = JSON.parse(raw) as { user?: { plan_access?: unknown } }
        planAccess = parsed.user?.plan_access ?? null
      }
      if (catalogBranchId) {
        await rememberUnpaidTakeawayOfflineLease({
          catalog: res.data,
          branchId: catalogBranchId,
          registerId: getSelectedPosRegisterId(),
          sessionId: getActiveRegisterSessionId(),
          offlineModeEnabled: readOfflineModeFromPlanAccess(planAccess),
        })
      }
    } catch {
      // Lease refresh must never fail catalog pull.
    }
  }
}

async function pushOrdersBatch(
  orders: posStore.PendingOfflineOrder[]
): Promise<{ pushed: number; errors: string[]; rows: PushOfflineOrderResult[] }> {
  if (!(await checkConnectivity())) {
    return { pushed: 0, errors: [], rows: [] }
  }

  const errors: string[] = []
  const done = new Set<string>()
  const rows: PushOfflineOrderResult[] = []
  const toSend: posStore.PendingOfflineOrder[] = []
  const dropBroken = new Set<string>()

  for (const raw of orders) {
    if (raw.status === 'syncing' && !isSyncingStale(raw)) {
      continue
    }
    const fixed = posStore.coercePendingOrderForSync(raw)
    if (!fixed) {
      const brokenReason =
        'طلب بدون بنود صالحة — يُحفظ في الطلبات الفاشلة (لا يمكن إصلاحه للمزامنة)'
      const id =
        typeof (raw as { client_uuid?: string })?.client_uuid === 'string'
          ? (raw as { client_uuid: string }).client_uuid
          : ''
      if (id) {
        dropBroken.add(id)
        if (raw && typeof raw === 'object' && (raw as posStore.PendingOfflineOrder).client_uuid) {
          await posStore.appendFailedOrder(raw as posStore.PendingOfflineOrder, brokenReason, 0)
          await logPosAuditEvent(id, 'push_failed', { reason: 'coerce_null' })
        }
      }
      errors.push(`${id || '—'}: ${brokenReason}`)
      continue
    }
    // Preserve the identity captured at checkout. Legacy missing identity is
    // sent unchanged so the backend can use sale_date safely or raise conflict.
    toSend.push(fixed)
  }

  if (dropBroken.size > 0) {
    await posStore.removePendingByClientUuids(dropBroken)
  }

  if (toSend.length === 0) {
    return { pushed: 0, errors, rows }
  }

  try {
    const failed = new Set<string>()
    const ordersByBranch = new Map<string, posStore.PendingOfflineOrder[]>()
    for (const order of toSend) {
      const branchId = String(order.branch_id || '').trim()
      if (!branchId) {
        errors.push(`${order.client_uuid}: طلب بدون فرع — لا يمكن مزامنته بأمان`)
        failed.add(order.client_uuid)
        await posStore.appendFailedOrder(order, 'Missing branch_id', 0)
        continue
      }
      const claimed = await posStore.markPendingOrderSyncing(order.client_uuid)
      if (!claimed) {
        continue
      }
      const bucket = ordersByBranch.get(branchId) ?? []
      bucket.push(order)
      ordersByBranch.set(branchId, bucket)
    }

    for (const [branchId, branchOrders] of Array.from(ordersByBranch.entries())) {
      if (branchOrders.length === 0) continue
      try {
        const res = await syncAPI.pushOfflineOrders(branchOrders, branchId)
        if (res.status !== 'success' || !Array.isArray(res.data)) {
          errors.push(res.message || `فشل مزامنة الفرع ${branchId}: استجابة غير صالحة من الخادم`)
          for (const order of branchOrders) {
            await posStore.resetPendingOrderStatus(order.client_uuid, 'pending')
          }
          continue
        }
        const handled = new Set<string>()
        for (const row of res.data) {
          rows.push(row)
          const matchedOrder = branchOrders.find(
            (o: posStore.PendingOfflineOrder) => o.client_uuid === row.client_uuid
          )
          if (!matchedOrder) {
            errors.push(`${row.client_uuid || '—'}: استجابة لمعرف غير موجود في الدفعة`)
            continue
          }
          handled.add(row.client_uuid)
          if (row.status === 'created' || row.status === 'duplicate') {
            // A status word alone is not durable proof. Never delete the local
            // invoice unless the server also returns its committed Sale id.
            if (row.sale_id === null || row.sale_id === undefined || String(row.sale_id).trim() === '') {
              errors.push(`${row.client_uuid}: إقرار مزامنة بلا معرف فاتورة محفوظة`)
              await posStore.resetPendingOrderStatus(row.client_uuid, 'pending')
              continue
            }
            rememberPushConfirmation(row)
            done.add(row.client_uuid)
            await logPosAuditEvent(row.client_uuid, 'push_confirmed', {
              status: row.status,
              sale_id: row.sale_id,
              branch_id: matchedOrder.branch_id,
              shift_id: matchedOrder.shift_id ?? null,
              pos_register_id: matchedOrder.pos_register_id ?? null,
              register_session_id: matchedOrder.register_session_id ?? null,
            })
            if (isSuspiciousOfflineSyncRow(row, matchedOrder?.print_sequence)) {
              showWarning(
                i18n.t('pos.cart.offlineSyncPrintSequenceMismatch', {
                  localSeq: matchedOrder?.print_sequence,
                  serverSeq: row.print_sequence
                }) as string
              )
            }
          } else if ((row.status === 'error' || row.status === 'conflict') && row.message) {
            const isConflict = row.status === 'conflict' || Boolean(row.code)
            errors.push(`${row.client_uuid}: ${row.message}`)
            const failedOrder = branchOrders.find(
              (o: posStore.PendingOfflineOrder) => o.client_uuid === row.client_uuid
            )
            if (failedOrder) {
              failed.add(row.client_uuid)
              const annotated = {
                ...failedOrder,
                status: isConflict ? ('conflict' as const) : ('failed' as const),
                conflict_code: row.code ?? null,
                last_error: row.message
              }
              await posStore.appendFailedOrder(annotated, row.message, 0)
            } else {
              // Deterministic conflicts must not stay in infinite auto-retry as "pending".
              await posStore.resetPendingOrderStatus(
                row.client_uuid,
                isConflict ? 'conflict' : 'pending'
              )
            }
          } else if (row.client_uuid) {
            errors.push(
              `${row.client_uuid}: استجابة غير متوقعة من الخادم (${row.status ?? 'unknown'})`
            )
            await posStore.resetPendingOrderStatus(row.client_uuid, 'pending')
          }
        }
        // A partial/malformed batch response must not strand the omitted local
        // invoice in `syncing` until the stale timer expires.
        for (const order of branchOrders) {
          if (!handled.has(order.client_uuid)) {
            errors.push(`${order.client_uuid}: الخادم لم يُرجع نتيجة لهذا الطلب`)
            await posStore.resetPendingOrderStatus(order.client_uuid, 'pending')
          }
        }
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        errors.push(msg || (e instanceof Error ? e.message : 'sync failed'))
        void recordClientError('offline_order_batch_push_failed', e, {
          category: 'sync',
          metadata: { phase: 'push_pending', retry_count: 0 },
        })
        for (const order of branchOrders) {
          await posStore.resetPendingOrderStatus(order.client_uuid, 'pending')
          await logPosAuditEvent(order.client_uuid, 'push_failed', {
            reason: 'transport_or_http_failure',
            branch_id: order.branch_id,
            shift_id: order.shift_id ?? null,
            pos_register_id: order.pos_register_id ?? null,
            register_session_id: order.register_session_id ?? null,
          })
        }
      }
    }
    const toRemove = new Set<string>()
    done.forEach((u) => toRemove.add(u))
    failed.forEach((u) => toRemove.add(u))
    await posStore.removePendingByClientUuids(toRemove)
    await publishPosDeviceSyncHeartbeat()
    try {
      await flushPosAuditEvents()
    } catch {
      // Audit upload is best-effort and must never change financial sync outcome.
    }
    return { pushed: done.size, errors, rows }
  } catch (e: unknown) {
    const msg =
      e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
    errors.push(msg || (e instanceof Error ? e.message : 'sync failed'))
    void recordClientError('offline_order_push_pipeline_failed', e, {
      category: 'sync', metadata: { phase: 'push_pipeline' },
    })
    for (const order of toSend) {
      await posStore.resetPendingOrderStatus(order.client_uuid, 'pending')
    }
    return { pushed: 0, errors, rows }
  }
}

export async function pushSingleOrder(
  order: posStore.PendingOfflineOrder
): Promise<{ row?: PushOfflineOrderResult; errors: string[] }> {
  return withPushMutex(async () => {
    const alreadyConfirmed = recentPushConfirmation(order.client_uuid)
    if (alreadyConfirmed) {
      return { row: alreadyConfirmed, errors: [] }
    }
    const result = await pushOrdersBatch([order])
    const row = result.rows.find((r) => r.client_uuid === order.client_uuid)
      ?? recentPushConfirmation(order.client_uuid)
    return { row, errors: result.errors }
  })
}

export async function pushPending(): Promise<{ pushed: number; errors: string[] }> {
  return withPushMutex(async () => {
    const pending = await posStore.getPendingOrders()
    if (pending.length === 0) {
      return { pushed: 0, errors: [] }
    }
    const result = await pushOrdersBatch(pending)
    return { pushed: result.pushed, errors: result.errors }
  })
}

export async function syncAllModules(): Promise<void> {
  if (!(await checkConnectivity())) return
  const dispatch = store.dispatch
  const fetches = [
    fetchCategories({}),
    fetchProducts({}),
    fetchCustomers({}),
    fetchSuppliers({}),
    fetchPurchases({}),
    fetchExpenses({}),
    fetchDeliveries({})
  ]
  for (const thunk of fetches) {
    try {
      // Shared hosting can reject a burst of tenant DB connections. Keep this
      // non-critical catalog/list refresh sequential and always run it after
      // the financially critical offline POS push above.
      await dispatch(thunk as any)
    } catch {}
  }
}

async function handleOnlineSync(): Promise<void> {
  if (syncing) return
  syncing = true
  try {
    if (!(await checkConnectivity())) return
    try {
      const queueResult = await replayQueue()
      if (queueResult.errors.length) {
        console.warn('Queue replay errors:', queueResult.errors)
      }
    } catch (e) {
      console.warn('Queue replay failed:', e)
    }
    try {
      const tableResult = await flushTableOps()
      if (tableResult.errors.length) {
        console.warn('Table ops flush errors:', tableResult.errors)
      }
    } catch (e) {
      console.warn('Table ops flush failed:', e)
    }
    try {
      const takeawayResult = await flushPendingTakeawayJournal()
      if (takeawayResult.errors.length) {
        console.warn('Pending takeaway journal flush errors:', takeawayResult.errors)
      }
    } catch (e) {
      console.warn('Pending takeaway journal flush failed:', e)
    }
    try {
      const failedBeforePush = await posStore.getFailedCount()
      if (failedBeforePush > 0) {
        await posStore.moveAllFailedOrdersToPending()
      }
      const result = await pushPending()
      if (result.pushed > 0) {
        const scope = posStore.resolveOfflineScope()
        if (scope.branchId && scope.branchId !== 'unknown') {
          await pull(scope.branchId)
          if (typeof window !== 'undefined') window.dispatchEvent(new Event('pos:catalog-updated'))
        }
      }
    } catch (e) {
      console.warn('POS push failed:', e)
    }
    try {
      await publishPosDeviceSyncHeartbeat()
    } catch (e) {
      console.warn('Device sync heartbeat failed:', e)
    }
    try {
      // A completed sale may already have left the pending queue before a
      // printer/reload event was written. Flush audit even when no sale awaits sync.
      await flushPosAuditEvents()
    } catch (e) {
      console.warn('POS audit upload failed:', e)
    }
    try {
      if (shouldRunModuleFanOut()) {
        await syncAllModules()
      }
    } catch (e) {
      console.warn('Sync all modules failed:', e)
    }
  } finally {
    syncing = false
  }
}

export function startAutoSync(): void {
  if (pushIntervalId != null) return

  onlineHandler = () => {
    void handleOnlineSync()
  }
  window.addEventListener('online', onlineHandler)
  const intervalMs = AUTO_SYNC_INTERVAL_MS + Math.floor(Math.random() * AUTO_SYNC_JITTER_MS)
  pushIntervalId = setInterval(() => {
    // Always re-probe the API. A failed ping sets the cached state to offline,
    // and the browser does not emit another `online` event when only the API
    // (rather than the network interface) recovers. Guarding this timer with
    // the stale cached value could therefore strand sales until a reload.
    void handleOnlineSync()
  }, intervalMs)
  // Do not leave pre-existing offline sales waiting for the first interval
  // after login/app reload. handleOnlineSync also publishes the heartbeat.
  void handleOnlineSync()
}

export function stopAutoSync(): void {
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler)
    onlineHandler = null
  }
  if (pushIntervalId != null) {
    clearInterval(pushIntervalId)
    pushIntervalId = null
  }
}
